import {
  PMS_CONTACT_BINDING_INFLIGHT_POLL_INTERVAL_MS,
  PMS_CONTACT_BINDING_MAX_INFLIGHT_POLLS
} from '@/lib/constants';
import type { PmsContactBinding } from './types';

/**
 * What a stored PMS contact-type binding currently *means*, as one of three states.
 *
 * The wire shape keeps the user's selection and the bind outcome in separate fields on purpose
 * (see {@link PmsContactBinding}), so every surface that shows a binding has to collapse
 * `isBound` / `lastError` into a state before it can render one. **This is the single place that
 * collapse happens** — the contacts listing's badge, its poll rule, and the contact drawer's
 * binding row all read state from this module rather than re-deriving it, because independent
 * readings of the same two fields is how surfaces end up disagreeing about what "not yet filed"
 * looks like. The listing badge and the drawer row both read the four-way
 * {@link PmsContactBindingDetailState} below (VALE-291); this coarser three-way type remains for
 * callers with no use for the fourth case.
 *
 * - `filed` — the contact is actually on the matter in the PMS under the chosen type.
 * - `in-flight` — the selection is recorded and nothing has failed, but the bind has not landed.
 * - `failed` — the last bind attempt errored; the selection survives so it can be retried.
 */
export type PmsContactBindingState = 'filed' | 'in-flight' | 'failed';

/**
 * The same collapse, keeping the fourth case a surface with room to explain it
 * can show.
 *
 * `filed-with-error` is a row that is **on the matter in the PMS and also
 * carrying a bind error**. It is a real, reachable state rather than an
 * inconsistency: `recordBindFailure` deliberately never clears `is_bound`,
 * because a failed *re*-bind does not unfile a contact that is already filed.
 *
 * **Do not assume the error is the older of the two.** The table records
 * `bound_at` but has no `errored_at`, so the two cannot be ordered from the row
 * itself — and in practice the failure is routinely the *newer* write: on the
 * rows observed while building this, `updated_at` ran a few milliseconds
 * **after** `bound_at`, i.e. a re-assert bound the contact and then a second
 * attempt failed. Treating it as stale therefore discards the most recent
 * outcome.
 */
export type PmsContactBindingDetailState =
  | PmsContactBindingState
  | 'filed-with-error';

/**
 * Collapse a binding into its full state, including {@link
 * PmsContactBindingDetailState}'s fourth case.
 */
export function getPmsContactBindingDetailState(
  binding: PmsContactBinding
): PmsContactBindingDetailState {
  if (binding.isBound) return binding.lastError ? 'filed-with-error' : 'filed';
  if (binding.lastError) return 'failed';
  return 'in-flight';
}

/**
 * Collapse a binding into the coarser three-way state — `filed-with-error`
 * folded into `filed`.
 *
 * **Not what the listing badge or the drawer row render** — both show the
 * fourth case directly via {@link getPmsContactBindingDetailState} (VALE-291).
 * This narrower collapse exists for a caller that only needs to ask "is this
 * still landing?", namely {@link isPmsContactBindingInFlight} below, and has
 * no use for the distinction the fourth case draws. Derived from
 * {@link getPmsContactBindingDetailState} rather than re-deriving the fields,
 * so the two can never disagree about the three states they share.
 */
export function getPmsContactBindingState(
  binding: PmsContactBinding
): PmsContactBindingState {
  const state = getPmsContactBindingDetailState(binding);
  return state === 'filed-with-error' ? 'filed' : state;
}

/**
 * Whether a binding is still waiting to be filed — the condition the listing's poll runs on.
 *
 * Note this is **not** a promise that it will ever settle: a contact that is not itself synced to
 * the PMS has its bind skipped, so its row stays in flight until that changes. The poll is bounded
 * for exactly that reason (`PMS_CONTACT_BINDING_MAX_INFLIGHT_POLLS`).
 */
export function isPmsContactBindingInFlight(
  binding: PmsContactBinding
): boolean {
  return getPmsContactBindingState(binding) === 'in-flight';
}

/**
 * How long until the bindings query should refetch, given what it currently holds — the poll rule
 * behind `usePmsMatterContactBindings`.
 *
 * **Pure, and living here rather than in the hook, so the rule is assertable on its own.** The
 * interval *is* the behaviour, and proving "polls while something is in flight, stops when nothing
 * is" through fake timers and a QueryClient would test React Query rather than the rule.
 *
 * Two independent stop conditions, both load-bearing:
 * - **nothing in flight** — the ordinary case, and why a settled listing costs exactly one request.
 * - **the poll ceiling** — because in-flight is not necessarily transient (see
 *   {@link isPmsContactBindingInFlight}); without it, one contact that is not synced to the PMS
 *   would keep a 5-second poll running for the life of the tab.
 *
 * @param bindings the query's current data
 * @param successfulFetchCount how many times the query has successfully fetched (React Query's
 *   `dataUpdateCount`), which is what bounds the poll
 */
export function pmsContactBindingsPollInterval(
  bindings: PmsContactBinding[] | undefined,
  successfulFetchCount: number
): number | false {
  if (!bindings?.some(isPmsContactBindingInFlight)) return false;
  if (successfulFetchCount >= PMS_CONTACT_BINDING_MAX_INFLIGHT_POLLS) {
    return false;
  }
  return PMS_CONTACT_BINDING_INFLIGHT_POLL_INTERVAL_MS;
}
